import { useState } from "react";
import { Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions } from "../lib/store";
import { qaDrawings, aiTakeoff } from "../lib/ai";
import { imageForScan } from "../lib/drawingScan";
import { fmtDate } from "../lib/format";
import type { Project, Deliverable, BoqItem } from "../lib/types";

/* AI QA audit — flag gaps, undrawn sheets, numbering breaks and coordination
   issues across the register before it's issued. */
const QA_HUE: Record<string, string> = { high: "var(--danger)", medium: "var(--warn)", low: "var(--ink-3)" };

export function QaModal({ p, sheets, by, onClose }: { p: Project; sheets: Deliverable[]; by: string; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const report = p.qa;

  async function run() {
    setBusy(true); setErr(null);
    const res = await qaDrawings(p.name, sheets.map((d) => ({ code: d.code, title: d.title, discipline: d.discipline, phase: d.phase, hasDrawing: !!(d.previewUrl || d.fileUrl), building: d.building })));
    setBusy(false);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The QA auditor isn't switched on (needs an AI key)." : "Couldn't run the audit."); return; }
    actions.setQaReport(p.id, { runOn: new Date().toISOString(), by, summary: res.summary ?? "", findings: (res.findings ?? []).map((f) => ({ severity: f.severity, category: f.category, sheet: f.sheet ?? undefined, finding: f.finding, suggestion: f.suggestion ?? undefined })) });
  }

  const findings = report?.findings ?? [];
  const rank: Record<string, number> = { high: 0, medium: 1, low: 2 };
  const sorted = [...findings].sort((a, b) => (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3));
  return (
    <Modal open onClose={onClose} title="Drawing-set QA audit" wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55, margin: 0, maxWidth: 460 }}>An AI pass over the register — flags undrawn sheets, numbering gaps, thin discipline coverage, coordination and consistency issues. Judgement, not gospel; verify before acting.</p>
          <button className="btn accent sm" disabled={busy} onClick={run}><Icon.spark width={14} />{busy ? "Auditing…" : report ? "Re-run audit" : "Run audit"}</button>
        </div>
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        {busy && <div className="row" style={{ gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />Reading the register…</div>}
        {report && !busy && (
          <>
            <div className="panel" style={{ padding: "11px 14px" }}>
              <div className="kicker" style={{ fontSize: 8.5, marginBottom: 4 }}>Summary · {fmtDate(report.runOn)}</div>
              <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>{report.summary}</div>
            </div>
            {sorted.length === 0 ? (
              <div className="panel" style={{ padding: 20, textAlign: "center" }}><Icon.check width={20} style={{ color: "var(--ok)" }} /><div style={{ fontWeight: 600, marginTop: 6 }}>No issues flagged</div></div>
            ) : (
              <div className="col" style={{ gap: 8, maxHeight: "46vh", overflowY: "auto" }}>
                {sorted.map((f, i) => (
                  <div key={i} className="panel" style={{ padding: "10px 13px", borderLeft: `3px solid ${QA_HUE[f.severity]}` }}>
                    <div className="row" style={{ gap: 8, marginBottom: 4 }}>
                      <span className="badge" style={{ fontSize: 8.5, color: QA_HUE[f.severity] }}>{f.severity}</span>
                      <span className="badge" style={{ fontSize: 8.5 }}>{f.category}</span>
                      {f.sheet && <span className="mono dim" style={{ fontSize: 11 }}>{f.sheet}</span>}
                    </div>
                    <div style={{ fontSize: 13.5, lineHeight: 1.5 }}>{f.finding}</div>
                    {f.suggestion && <div className="dim" style={{ fontSize: 12, marginTop: 3 }}>→ {f.suggestion}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        {!report && !busy && !err && <div className="dim" style={{ padding: 16, textAlign: "center", fontSize: 13 }}>Run the audit to check {sheets.length} sheet{sheets.length === 1 ? "" : "s"}.</div>}
      </div>
    </Modal>
  );
}

/* AI take-off → editable bill of quantities. */
export function BoqModal({ p, sheets, by, onClose }: { p: Project; sheets: Deliverable[]; by: string; onClose: () => void }) {
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const boq = p.boq ?? [];

  async function generate() {
    setBusy(true); setErr(null); setProg("Preparing sheets…");
    const drawn = sheets.filter((d) => d.previewUrl || d.fileUrl).slice(0, 8);
    const withImg: { code: string; title: string; discipline: string; image: string }[] = [];
    for (const d of drawn) { const img = d.previewUrl ? await imageForScan(d.previewUrl) : null; if (img) withImg.push({ code: d.code, title: d.title, discipline: d.discipline, image: img }); }
    if (!withImg.length) { setBusy(false); setProg(null); setErr("No drawn sheets with a preview to measure."); return; }
    setProg(`Reading ${withImg.length} sheet${withImg.length === 1 ? "" : "s"}…`);
    const res = await aiTakeoff(withImg);
    setBusy(false); setProg(null);
    if (!res.ok) { setErr(res.reason === "no-key" ? "The take-off isn't switched on (needs an AI key)." : "Couldn't read quantities."); return; }
    const items: BoqItem[] = (res.items ?? []).map((it) => ({ id: `bq-${Math.random().toString(36).slice(2, 9)}`, code: it.code ?? undefined, discipline: it.discipline ?? undefined, description: it.description, qty: it.qty, unit: it.unit, note: it.note ?? undefined, source: "ai" as const }));
    actions.setBoq(p.id, [...items, ...boq], by);
  }

  function exportCsv() {
    const rows = [["Code", "Discipline", "Description", "Qty", "Unit", "Note"], ...boq.map((i) => [i.code ?? "", i.discipline ?? "", i.description, String(i.qty), i.unit, i.note ?? ""])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${(p.code || p.name).replace(/[^\w]+/g, "_")}_BOQ.csv`; a.click();
  }

  return (
    <Modal open onClose={onClose} title="Bill of quantities (AI take-off)" wide>
      <div className="col" style={{ gap: 14 }}>
        <div className="row between wrap" style={{ gap: 10 }}>
          <p className="muted" style={{ fontSize: 13.5, lineHeight: 1.55, margin: 0, maxWidth: 440 }}>Reads measurable quantities off the drawings into an editable BOQ. A preliminary estimate — always verify against the drawings. Samples up to 8 sheets per run.</p>
          <div className="row" style={{ gap: 8 }}>
            {boq.length > 0 && <button className="btn ghost sm" onClick={exportCsv}><Icon.download width={13} />CSV</button>}
            <button className="btn accent sm" disabled={busy} onClick={generate}><Icon.spark width={14} />{busy ? "Reading…" : boq.length ? "Add more" : "Generate"}</button>
          </div>
        </div>
        {prog && <div className="row" style={{ gap: 8, color: "var(--ink-3)", fontSize: 13 }}><span className="dot" />{prog}</div>}
        {err && <div style={{ color: "var(--danger)", fontSize: 12.5 }}>{err}</div>}
        {boq.length === 0 && !busy && <div className="dim" style={{ padding: 16, textAlign: "center", fontSize: 13 }}>No quantities yet — generate to read them from the drawings.</div>}
        {boq.length > 0 && (
          <div style={{ overflowX: "auto", maxHeight: "50vh", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 560 }}>
              <thead><tr>{["Item", "Description", "Qty", "Unit", ""].map((h, i) => <th key={i} style={{ textAlign: i === 2 ? "right" : "left", padding: "8px 10px", borderBottom: "1px solid var(--line)", color: "var(--ink-3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap" }}>{h}</th>)}</tr></thead>
              <tbody>
                {boq.map((it) => (
                  <tr key={it.id} style={{ borderBottom: "1px solid var(--line)" }}>
                    <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}><span className="mono dim" style={{ fontSize: 11 }}>{it.code || "—"}</span><div className="dim" style={{ fontSize: 10 }}>{it.discipline}</div></td>
                    <td style={{ padding: "6px 10px" }}><input className="input" style={{ fontSize: 12.5, padding: "4px 8px" }} value={it.description} onChange={(e) => actions.updateBoqItem(p.id, it.id, { description: e.target.value })} /></td>
                    <td style={{ padding: "6px 10px", width: 90 }}><input className="input mono" style={{ fontSize: 12.5, padding: "4px 8px", textAlign: "right" }} type="number" value={it.qty} onChange={(e) => actions.updateBoqItem(p.id, it.id, { qty: Number(e.target.value) })} /></td>
                    <td style={{ padding: "6px 10px", width: 70 }}><input className="input" style={{ fontSize: 12.5, padding: "4px 8px", width: 60 }} value={it.unit} onChange={(e) => actions.updateBoqItem(p.id, it.id, { unit: e.target.value })} /></td>
                    <td style={{ padding: "6px 10px" }}><button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => actions.removeBoqItem(p.id, it.id)}><Icon.close width={12} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="dim" style={{ fontSize: 11, marginTop: 8 }}>{boq.length} item{boq.length === 1 ? "" : "s"} · {boq.filter((i) => i.source === "ai").length} from AI. Edit any figure; export to CSV for your estimate.</div>
          </div>
        )}
      </div>
    </Modal>
  );
}
